import React, { useState, useEffect } from 'react';
import { AccountIcon, ProductCatalogIcon, DiscountIcon } from '../../../public/icons';
import adminService from '../../../services/admin/admin-authService';
import { useAdminAuth } from '../../../context/AdminAuthContext';

const RolesPermissionsTab = () => {
  const { admin } = useAdminAuth();
  const [selectedRole, setSelectedRole] = useState('super-admin');
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  const roles = [
    {
      id: 'super-admin',
      name: 'Super Admin',
      description: 'Full platform access'
    },
    {
      id: 'operations',
      name: 'Operations Manager',
      description: 'Orders, fulfilment & delivery'
    },
    {
      id: 'support',
      name: 'Support Manager',
      description: 'Customer support & conversations'
    },
    {
      id: 'customer-service',
      name: 'Customer Service',
      description: 'Customer service and order management'
    }
  ];

  const permissionSections = [
    {
      id: 'products',
      title: 'Products & Catalog',
      icon: <DiscountIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1E1E1E]" color='#1E1E1E' />,
      permissions: [
        { id: 'editProducts', label: 'Edit Products' },
        { id: 'createProducts', label: 'Create Products' },
        { id: 'deleteProducts', label: 'Delete Products' },
        { id: 'manageCollections', label: 'Manage Collections' }
      ]
    },
    {
      id: 'orders',
      title: 'Orders & Fulfilment',
      icon: <ProductCatalogIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1C2A46]" />,
      permissions: [
        { id: 'viewOrders', label: 'View Orders' },
        { id: 'processOrders', label: 'Process Orders' },
        { id: 'manageShipping', label: 'Manage Shipping' },
        { id: 'issueRefunds', label: 'Issue Refunds' }
      ]
    },
    {
      id: 'customers',
      title: 'Customers',
      icon: <ProductCatalogIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1C2A46]" />,
      permissions: [
        { id: 'viewCustomersData', label: 'View Customers Data' },
        { id: 'exportCustomerData', label: 'Export Customer Data' },
        { id: 'editCustomerProfiles', label: 'Edit customer profiles' }
      ]
    },
    {
      id: 'financials',
      title: 'Financials',
      icon: <ProductCatalogIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1C2A46]" />,
      permissions: [
        { id: 'viewPayments', label: 'View payments & transactions' },
        { id: 'processRefunds', label: 'Process refunds' },
        { id: 'managePricing', label: 'Manage pricing & discounts' }
      ]
    }
  ];

  useEffect(() => {
    fetchPermissions();
  }, [selectedRole]);

  const fetchPermissions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await adminService.getRolePermissions(selectedRole);
      if (response.success) {
        setPermissions(response.data.permissions);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError('Failed to load permissions');
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (section, permission) => {
    if (!permissions || selectedRole === 'super-admin') return;
    
    setPermissions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [permission]: !prev[section][permission]
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (selectedRole === 'super-admin') {
      setError('Super Admin permissions cannot be modified');
      return;
    }

    setSaving(true);
    setSaveSuccess(false);
    setError('');

    try {
      const response = await adminService.updateRolePermissions(selectedRole, permissions);
      
      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        
        const currentUser = adminService.getCurrentUser();
        const roleMap = {
          'super-admin': 'Super Admin',
          'operations': 'Operations Manager',
          'support': 'Support Manager',
          'customer-service': 'Customer Service'
        };
        
        if (currentUser.role === roleMap[selectedRole]) {
          const profileResponse = await adminService.getProfile();
          if (profileResponse.success) {
            localStorage.setItem('adminUser', JSON.stringify(profileResponse.data));
          }
        }
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError('Failed to save permissions');
      console.error('Error saving permissions:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (selectedRole === 'super-admin') return;
    
    if (window.confirm('Reset to default permissions?')) {
      try {
        const response = await adminService.getRolePermissions(selectedRole);
        if (response.success && response.data.isDefault) {
          setPermissions(response.data.permissions);
        } else {
          const defaultPermissions = getDefaultPermissions(selectedRole);
          setPermissions(defaultPermissions);
        }
      } catch (error) {
        console.error('Error resetting permissions:', error);
      }
    }
  };

  const getDefaultPermissions = (roleId) => {
    const defaults = {
      'operations': {
        products: { editProducts: true, createProducts: true, deleteProducts: true, manageCollections: true },
        orders: { viewOrders: true, processOrders: true, manageShipping: true, issueRefunds: false },
        customers: { viewCustomersData: true, exportCustomerData: false, editCustomerProfiles: false },
        financials: { viewPayments: true, processRefunds: false, managePricing: true }
      },
      'support': {
        products: { editProducts: false, createProducts: false, deleteProducts: false, manageCollections: false },
        orders: { viewOrders: true, processOrders: false, manageShipping: false, issueRefunds: true },
        customers: { viewCustomersData: true, exportCustomerData: false, editCustomerProfiles: true },
        financials: { viewPayments: false, processRefunds: true, managePricing: false }
      },
      'customer-service': {
        products: { editProducts: false, createProducts: false, deleteProducts: false, manageCollections: false },
        orders: { viewOrders: true, processOrders: true, manageShipping: false, issueRefunds: false },
        customers: { viewCustomersData: true, exportCustomerData: false, editCustomerProfiles: true },
        financials: { viewPayments: false, processRefunds: false, managePricing: false }
      }
    };
    
    return defaults[roleId] || defaults.operations;
  };

  const isSuperAdmin = admin?.role === 'Super Admin';

  if (!isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white p-4 sm:p-6 rounded-lg">
          <div className="text-center py-6 sm:py-10">
            <h3 className="text-lg sm:text-xl font-medium text-gray-700 mb-2">Access Denied</h3>
            <p className="text-sm text-gray-500">Only Super Admins can manage roles and permissions.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 px-2 sm:px-0">
      {/* Roles Sidebar */}
      <div className="w-full lg:w-80 bg-white rounded-sm p-4 sm:p-6 text-left">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <AccountIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1C2A46]" />
          <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">Roles</h2>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {roles.map((role) => (
            <div
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={`p-3 sm:p-4 rounded-lg cursor-pointer transition-colors ${
                selectedRole === role.id
                  ? 'bg-[#FFEDF0] border-l-4 border-[#55000C]'
                  : 'hover:bg-gray-50'
              } ${role.id === 'super-admin' ? 'opacity-80' : ''}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-medium text-[#0E1628] mb-0.5 sm:mb-1 text-sm sm:text-base">{role.name}</h3>
                {role.id === 'super-admin' && (
                  <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">Full Access</span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-[#6B635D]">{role.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Permissions Panel */}
      <div className="flex-1 bg-white rounded-sm p-4 sm:p-6 text-left overflow-x-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-[#0E1628] mb-1 sm:mb-2">
              {roles.find(role => role.id === selectedRole)?.name}
            </h2>
            <p className="text-xs sm:text-sm text-[#6B635D]">Choose what this role can access.</p>
          </div>
          
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            {selectedRole !== 'super-admin' && (
              <>
                <button
                  onClick={handleResetToDefaults}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs sm:text-sm"
                >
                  Reset to Defaults
                </button>
                <button
                  onClick={handleSavePermissions}
                  disabled={saving || loading}
                  className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-white hover:bg-[#C49660] text-xs sm:text-sm ${
                    saving || loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {saveSuccess && (
          <div className="mb-4 p-2 sm:p-3 bg-green-50 text-green-700 rounded text-xs sm:text-sm">
            ✓ Permissions updated successfully
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-2 sm:p-3 bg-red-50 text-red-700 rounded text-xs sm:text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8 sm:py-10">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-gray-600 text-sm">Loading permissions...</span>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8 text-left">
            {permissionSections.map((section) => (
              <div key={section.id} className="border-b border-[#DBDBDB] pb-4 sm:pb-6 text-left">
                <div className="flex gap-2 sm:gap-3 items-center mb-3 sm:mb-4 text-left">
                  {section.icon}
                  <h3 className="text-base sm:text-lg font-normal text-[#0E1628]">{section.title}</h3>
                </div>

                <div className="flex flex-col space-y-3 sm:space-y-4">
                  {section.permissions.map((permission) => (
                    <div key={permission.id} className="flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={permissions?.[section.id]?.[permission.id] || false}
                          onChange={() => handlePermissionToggle(section.id, permission.id)}
                          disabled={selectedRole === 'super-admin' || saving}
                          className="sr-only peer"
                        />
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 border-2 border-[#1C2A46] rounded peer-checked:bg-[#1C2A46] peer-checked:border-[#1C2A46] flex items-center justify-center ${
                          selectedRole === 'super-admin' ? 'opacity-50' : ''
                        }`}>
                          {permissions?.[section.id]?.[permission.id] && (
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </label>
                      <span className={`ml-2 sm:ml-3 text-[#8A9099] font-normal text-xs sm:text-sm ${
                        selectedRole === 'super-admin' ? 'opacity-50' : ''
                      }`}>
                        {permission.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {selectedRole === 'super-admin' && (
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 text-blue-700 rounded">
            <p className="text-xs sm:text-sm">Super Admin has full access to all features. Permissions cannot be modified.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesPermissionsTab;